"use client"

// src/alert.tsx
import {
  forwardRef,
  omitThemeProps,
  ui,
  useComponentMultiStyle,
  useTheme
} from "@yamada-ui/core";
import { CheckIcon, InfoIcon, WarningIcon } from "@yamada-ui/icon";
import { Loading } from "@yamada-ui/loading";
import { createContext, cx } from "@yamada-ui/utils";
import { jsx } from "react/jsx-runtime";
var defaultStatuses = {
  error: { colorScheme: "danger", icon: WarningIcon },
  info: { colorScheme: "info", icon: InfoIcon },
  loading: { colorScheme: "primary", icon: Loading },
  success: { colorScheme: "success", icon: CheckIcon },
  warning: { colorScheme: "warning", icon: WarningIcon }
};
var [AlertProvider, useAlert] = createContext({
  name: `AlertContext`,
  errorMessage: `useAlert returned is 'undefined'. Seems you forgot to wrap the components in "<Alert />" `
});
var getStatusColorScheme = (status, statuses) => {
  var _a, _b;
  return (_b = (_a = statuses == null ? void 0 : statuses[status]) == null ? void 0 : _a.colorScheme) != null ? _b : defaultStatuses[status].colorScheme;
};
var getStatusIcon = (status, statuses) => {
  var _a, _b;
  return (_b = (_a = statuses == null ? void 0 : statuses[status]) == null ? void 0 : _a.icon) != null ? _b : defaultStatuses[status].icon;
};
var Alert = forwardRef(
  ({ colorScheme, status = "info", ...props }, ref) => {
    var _a, _b, _c;
    const { theme } = useTheme();
    const statuses = (_c = (_b = (_a = theme.__config) == null ? void 0 : _a.alert) == null ? void 0 : _b.statuses) != null ? _c : {};
    colorScheme != null ? colorScheme : colorScheme = getStatusColorScheme(status, statuses);
    const [styles, mergedProps] = useComponentMultiStyle("Alert", {
      ...props,
      colorScheme
    });
    const { className, children, ...rest } = omitThemeProps(mergedProps);
    const css = {
      alignItems: "center",
      display: "flex",
      overflow: "hidden",
      position: "relative",
      w: "100%",
      ...styles.container
    };
    return /* @__PURE__ */ jsx(AlertProvider, { value: { status, styles }, children: /* @__PURE__ */ jsx(
      ui.div,
      {
        ref,
        className: cx("ui-alert", className),
        role: "alert",
        __css: css,
        ...rest,
        children
      }
    ) });
  }
);
Alert.displayName = "Alert";
Alert.__ui__ = "Alert";
var AlertIcon = ({
  className,
  variant = "oval",
  children,
  ...rest
}) => {
  var _a, _b, _c;
  const { status, styles } = useAlert();
  const { theme } = useTheme();
  const statuses = (_c = (_b = (_a = theme.__config) == null ? void 0 : _a.alert) == null ? void 0 : _b.statuses) != null ? _c : {};
  const Icon = getStatusIcon(status, statuses);
  const css = {
    ...styles.icon,
    ...status === "loading" ? styles.loading : {}
  };
  return /* @__PURE__ */ jsx(
    ui.span,
    {
      className: cx("ui-alert__icon", className),
      display: "inherit",
      __css: css,
      ...rest,
      children: children || /* @__PURE__ */ jsx(
        Icon,
        {
          ...status === "loading" ? { variant, color: "currentcolor" } : { boxSize: "100%" }
        }
      )
    }
  );
};
AlertIcon.displayName = "AlertIcon";
AlertIcon.__ui__ = "AlertIcon";
var AlertTitle = forwardRef(
  ({ className, ...rest }, ref) => {
    const { styles } = useAlert();
    const css = {
      display: "block",
      ...styles.title
    };
    return /* @__PURE__ */ jsx(
      ui.p,
      {
        ref,
        className: cx("ui-alert__title", className),
        __css: css,
        ...rest
      }
    );
  }
);
AlertTitle.displayName = "AlertTitle";
AlertTitle.__ui__ = "AlertTitle";
var AlertDescription = forwardRef(
  ({ className, ...rest }, ref) => {
    const { styles } = useAlert();
    const css = {
      ...styles.description
    };
    return /* @__PURE__ */ jsx(
      ui.span,
      {
        ref,
        className: cx("ui-alert__desc", className),
        __css: css,
        ...rest
      }
    );
  }
);
AlertDescription.displayName = "AlertDescription";
AlertDescription.__ui__ = "AlertDescription";

export {
  getStatusColorScheme,
  getStatusIcon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription
};
//# sourceMappingURL=chunk-W5JFQ7LF.mjs.map